// BrainMap — the wiki as a map. One component for the plane (/admin/brain/map)
// and the kit (/brain?view=map); byte-identical in both repos (scope: both).
//
// Input is the graph the summariser writes (scripts/lib/brain-summary.mjs
// `graph`): nodes with the index blurb and outline, wikilink edges, log
// touches. The kit merges strata (app + kit) and namespaces node keys
// `<stratum>:<slug>`; the plane passes its one brain with slugs as keys.
//
//   <BrainMap graph={graph} onOpen={(node) => …} onSearch={async (q) => keys}
//             heat={{ [key]: served }}
//             phases={{ [key]: 'candidate'|'selected'|'cited'|'opened' }}
//             pulse={Set<key>} focus={key} chrome={true} />
//
// What it shows: clusters by page type (regions with dashed hulls), radius
// by inbound links, colour by type, stale/thin pages dimmed, error pages
// ringed, a hover card with the blurb and outline, a layers tree (strata →
// types → index sections), a neighbourhood zoom (1 hop / 2 hops / all), a
// "changed since" timeline over log touches + `updated`, a search box that
// lights matching pages (server scorer when onSearch is given, else local
// title/tags/blurb/outline match), and optional retrieval heat halos.
//
// The think player (BrainThink.js) drives the same map with `phases` (what
// a running search has done with each page: found it, kept it, cited it,
// opened it — everything else dims), `pulse` (the pages the current event
// touched, drawn with a glow) and `focus` (the page to centre on). `chrome`
// false hides the toolbar and layers for an embedded, read-only map.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import { forceCollide, forceX, forceY } from 'd3-force';
import './BrainMap.css';

export const TYPE_COLORS = {
  system: '#14e0c8', decision: '#f26d9b', lesson: '#ffa94d', runbook: '#7bd88f', concept: '#c7e35f', skill: '#a78bfa', other: '#98a2b3',
};
const TYPE_ORDER = ['system', 'decision', 'lesson', 'runbook', 'concept', 'skill', 'other'];
const typeOf = (n) => (TYPE_COLORS[n.type] ? n.type : 'other');
const keyOf = (n) => n.key || n.slug;
const endKey = (x) => (typeof x === 'object' && x ? (x.key || x.id || x.slug) : x);

// Cluster anchors: strata side by side, types on a grid inside each.
function anchorsFor(strata) {
  const out = {};
  strata.forEach((s, si) => {
    const offsetX = (si - (strata.length - 1) / 2) * 1500;
    TYPE_ORDER.forEach((t, i) => {
      const col = i % 4; const row = Math.floor(i / 4);
      out[`${s}|${t}`] = { x: offsetX + (col - 1.5) * 380, y: (row - 0.5) * 320 };
    });
  });
  return out;
}

const radiusOf = (n) => 3.5 + 11 * Math.pow(Math.min(n.backlinks || 0, 30) / 30, 0.6);
const labelHalf = (n) => Math.min((n.title || '').length, 26) * 3 + 6;

function convexHull(nodes) {
  const pts = [...nodes].sort((a, b) => a.x - b.x || a.y - b.y);
  if (pts.length < 3) return pts;
  const cross = (o, a, b) => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
  const lower = []; const upper = [];
  for (const p of pts) { while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop(); lower.push(p); }
  for (let i = pts.length - 1; i >= 0; i -= 1) { const p = pts[i]; while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop(); upper.push(p); }
  lower.pop(); upper.pop();
  return lower.concat(upper);
}

const cssVar = (name, fallback) => (typeof window !== 'undefined' ? getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback : fallback);

export default function BrainMap({ graph, onOpen, onSearch, heat, height = 620, initialSelected = null, phases = null, pulse = null, focus = null, chrome = true }) {
  const wrapRef = useRef(null);
  const fgRef = useRef(null);
  const [size, setSize] = useState({ width: 900, height });
  const [hover, setHover] = useState(null);
  const [selected, setSelected] = useState(initialSelected);
  const [hops, setHops] = useState(1);
  const [hiddenTypes, setHiddenTypes] = useState(() => new Set());
  const [hiddenStrata, setHiddenStrata] = useState(() => new Set());
  const [hiddenSections, setHiddenSections] = useState(() => new Set());
  const [since, setSince] = useState(null); // ISO date or null
  const [q, setQ] = useState('');
  const [lit, setLit] = useState(null); // Set of keys matched by search, or null
  const [showLayers, setShowLayers] = useState(chrome);
  const theme = useMemo(() => ({ bg: cssVar('--bg-inset', '#0e0e14'), text: cssVar('--text', '#f5f5f7'), muted: cssVar('--muted', '#9aa0a6'), ok: cssVar('--ok', '#2dd4bf'), danger: cssVar('--danger', '#ff6b6b'), warn: cssVar('--warn', '#e0a414'), info: cssVar('--info', '#4da3ff') }), []);
  const phased = phases && Object.keys(phases).length > 0;
  const light = useMemo(() => /^#(f|e)/i.test(theme.bg) || /^#f/i.test(cssVar('--bg', '#0b0b0f')), [theme]);

  // ---- normalise the graph once ---------------------------------------------
  const data = useMemo(() => {
    const nodes = (graph?.nodes || []).map((n) => ({ ...n, key: keyOf(n), stratum: n.stratum || 'brain', t: typeOf(n) }));
    const have = new Set(nodes.map((n) => n.key));
    const links = (graph?.edges || []).map((e) => ({ source: e.source ?? e.from, target: e.target ?? e.to })).filter((e) => have.has(e.source) && have.has(e.target));
    return { nodes, links };
  }, [graph]);
  const byKey = useMemo(() => new Map(data.nodes.map((n) => [n.key, n])), [data]);
  const strata = useMemo(() => [...new Set(data.nodes.map((n) => n.stratum))], [data]);
  const sections = useMemo(() => {
    const m = new Map();
    for (const n of data.nodes) { const k = `${n.stratum}|${n.section || '—'}`; m.set(k, (m.get(k) || 0) + 1); }
    return [...m.entries()].map(([k, count]) => { const [stratum, title] = k.split('|'); return { key: k, stratum, title, count }; });
  }, [data]);
  const anchors = useMemo(() => anchorsFor(strata), [strata]);
  const neighbors = useMemo(() => {
    const m = new Map();
    for (const l of data.links) {
      const s = endKey(l.source); const t = endKey(l.target);
      if (!m.has(s)) m.set(s, new Set()); if (!m.has(t)) m.set(t, new Set());
      m.get(s).add(t); m.get(t).add(s);
    }
    return m;
  }, [data]);
  // Last time each page was touched: max(updated, log touch dates).
  const touchedAt = useMemo(() => {
    const m = new Map();
    for (const n of data.nodes) if (n.updated) m.set(n.key, n.updated);
    for (const t of graph?.touches || []) {
      for (const k of t.keys || t.slugs || []) if (!m.get(k) || m.get(k) < t.date) m.set(k, t.date);
    }
    return m;
  }, [data, graph]);
  const dates = useMemo(() => [...new Set([...touchedAt.values()])].sort(), [touchedAt]);
  const maxHeat = useMemo(() => Math.max(1, ...Object.values(heat || {})), [heat]);

  // ---- which nodes are in play ------------------------------------------------
  const hood = useMemo(() => {
    if (!selected || hops === 0) return null;
    const seen = new Set([selected]); let frontier = [selected];
    for (let i = 0; i < hops; i += 1) {
      const next = [];
      for (const k of frontier) for (const nb of neighbors.get(k) || []) if (!seen.has(nb)) { seen.add(nb); next.push(nb); }
      frontier = next;
    }
    return seen;
  }, [selected, hops, neighbors]);
  const visible = useCallback((n) => !hiddenTypes.has(n.t) && !hiddenStrata.has(n.stratum) && !hiddenSections.has(`${n.stratum}|${n.section || '—'}`) && (!hood || hood.has(n.key)), [hiddenTypes, hiddenStrata, hiddenSections, hood]);
  const shown = useMemo(() => {
    const nodes = data.nodes.filter(visible);
    const keys = new Set(nodes.map((n) => n.key));
    return { nodes, links: data.links.filter((l) => keys.has(endKey(l.source)) && keys.has(endKey(l.target))) };
  }, [data, visible]);
  const hoverSet = hover ? neighbors.get(hover.key) || new Set() : null;
  const dimmed = (n) => {
    if (hover) return n.key !== hover.key && !hoverSet.has(n.key);
    if (lit) return !lit.has(n.key);
    if (phased) return !phases[n.key];
    if (since) return !(touchedAt.get(n.key) >= since);
    return false;
  };
  const phaseColor = (p) => (p === 'cited' ? theme.text : p === 'opened' ? theme.warn : p === 'selected' ? theme.ok : theme.muted);

  // ---- search ----------------------------------------------------------------
  useEffect(() => {
    const query = q.trim().toLowerCase();
    if (!query) { setLit(null); return undefined; }
    let live = true;
    const local = () => {
      const words = query.split(/[^a-z0-9]+/).filter((w) => w.length > 2);
      setLit(new Set(data.nodes.filter((n) => { const hay = `${n.slug} ${n.title} ${(n.tags || []).join(' ')} ${n.blurb || ''} ${(n.headings || []).join(' ')}`.toLowerCase(); return words.every((w) => hay.includes(w)); }).map((n) => n.key)));
    };
    const t = setTimeout(() => {
      if (!onSearch) return local();
      Promise.resolve(onSearch(query)).then((keys) => { if (live) setLit(new Set(keys || [])); }).catch(local);
      return undefined;
    }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [q, onSearch, data]);

  // ---- layout ----------------------------------------------------------------
  useEffect(() => {
    if (!wrapRef.current) return undefined;
    const ro = new ResizeObserver(([e]) => setSize({ width: e.contentRect.width, height }));
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, [height]);
  useEffect(() => {
    const fg = fgRef.current;
    if (!fg) return;
    fg.d3Force('charge').strength(-220).distanceMax(360);
    fg.d3Force('link').distance(60).strength(0.2);
    fg.d3Force('collide', forceCollide((n) => Math.max(radiusOf(n) + 10, labelHalf(n) * 0.7)));
    fg.d3Force('x', forceX((n) => (anchors[`${n.stratum}|${n.t}`] || { x: 0 }).x).strength(0.14));
    fg.d3Force('y', forceY((n) => (anchors[`${n.stratum}|${n.t}`] || { y: 0 }).y).strength(0.14));
    fg.d3ReheatSimulation();
    const t = setTimeout(() => fg.zoomToFit(400, 30), 900);
    return () => clearTimeout(t);
  }, [shown, anchors]);

  // The player's focus: glide to the page just opened.
  useEffect(() => {
    if (!focus || !fgRef.current) return;
    const n = byKey.get(focus);
    if (n && n.x != null) fgRef.current.centerAt(n.x, n.y, 600);
  }, [focus, byKey]);

  const select = (key, { center = false } = {}) => {
    setSelected(key);
    if (center && fgRef.current && key) { const n = byKey.get(key); if (n && n.x != null) fgRef.current.centerAt(n.x, n.y, 500); }
  };
  const toggle = (set, setter, v) => setter((prev) => { const n = new Set(prev); if (n.has(v)) n.delete(v); else n.add(v); return n; });

  const sel = selected ? byKey.get(selected) : null;
  const card = hover || sel;
  const selIn = sel ? data.links.filter((l) => endKey(l.target) === sel.key).map((l) => byKey.get(endKey(l.source))).filter(Boolean) : [];
  const selOut = sel ? data.links.filter((l) => endKey(l.source) === sel.key).map((l) => byKey.get(endKey(l.target))).filter(Boolean) : [];
  const typeCounts = useMemo(() => { const m = {}; for (const n of data.nodes) m[n.t] = (m[n.t] || 0) + 1; return m; }, [data]);

  if (!graph || !data.nodes.length) return <div className="bm-empty">No map yet — the graph is written by the brain sync / seeder.</div>;

  return (
    <div className={`bm ${chrome ? '' : 'bm-bare'}`} data-nova-note={`Brain map: ${data.nodes.length} pages, ${data.links.length} links${sel ? `; selected ${sel.title}` : ''}${phased ? `; ${Object.keys(phases).length} pages in the current search` : ''}`}>
      {chrome ? <div className="bm-toolbar">
        <input className="bm-search" type="search" placeholder="Search the map…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search the brain map" />
        <span className="bm-hint">{lit ? `${lit.size} lit` : `${shown.nodes.length} of ${data.nodes.length} pages · ${shown.links.length} links`}</span>
        {sel ? (
          <span className="bm-hops" role="group" aria-label="Neighbourhood">
            {[1, 2, 0].map((h) => <button key={h} type="button" className={`bm-chip ${hops === h ? 'is-on' : ''}`} onClick={() => setHops(h)}>{h === 0 ? 'all' : `${h} hop${h > 1 ? 's' : ''}`}</button>)}
          </span>
        ) : null}
        {dates.length > 1 ? (
          <label className="bm-time">
            <span>changed since {since || '—'}</span>
            <input type="range" min={0} max={dates.length} value={since ? dates.indexOf(since) + 1 : 0} onChange={(e) => { const i = Number(e.target.value); setSince(i ? dates[i - 1] : null); }} />
          </label>
        ) : null}
        <button type="button" className="bm-chip" onClick={() => setShowLayers((v) => !v)}>{showLayers ? 'hide layers' : 'layers'}</button>
        <button type="button" className="bm-chip" onClick={() => { setSelected(null); setQ(''); setSince(null); setHiddenTypes(new Set()); setHiddenStrata(new Set()); setHiddenSections(new Set()); }}>reset</button>
      </div> : null}
      <div className="bm-body">
        {showLayers ? (
          <aside className="bm-layers" aria-label="Layers">
            {strata.length > 1 ? (
              <div className="bm-layer-group">
                <h4>Strata</h4>
                {strata.map((s) => <button key={s} type="button" className={`bm-layer ${hiddenStrata.has(s) ? 'is-off' : ''}`} onClick={() => toggle(hiddenStrata, setHiddenStrata, s)}>{s === 'app' ? 'This app' : s === 'kit' ? 'The kit' : s}<span>{data.nodes.filter((n) => n.stratum === s).length}</span></button>)}
              </div>
            ) : null}
            <div className="bm-layer-group">
              <h4>Types</h4>
              {TYPE_ORDER.filter((t) => typeCounts[t]).map((t) => <button key={t} type="button" className={`bm-layer ${hiddenTypes.has(t) ? 'is-off' : ''}`} onClick={() => toggle(hiddenTypes, setHiddenTypes, t)}><i style={{ background: TYPE_COLORS[t] }} />{t}s<span>{typeCounts[t]}</span></button>)}
            </div>
            <div className="bm-layer-group bm-sections">
              <h4>Sections</h4>
              {sections.map((s) => <button key={s.key} type="button" className={`bm-layer ${hiddenSections.has(s.key) ? 'is-off' : ''}`} onClick={() => toggle(hiddenSections, setHiddenSections, s.key)} title={s.title}>{strata.length > 1 ? <em>{s.stratum} · </em> : null}{s.title}<span>{s.count}</span></button>)}
            </div>
            <div className="bm-legend">
              <div><i className="bm-leg-dim" />stale or thin</div>
              <div><i className="bm-leg-err" />lint error</div>
              {heat ? <div><i className="bm-leg-heat" />served by NoVA</div> : null}
            </div>
          </aside>
        ) : null}
        <div className="bm-canvas" ref={wrapRef} style={{ height }}>
          <ForceGraph2D
            ref={fgRef}
            width={size.width}
            height={size.height}
            graphData={shown}
            nodeId="key"
            backgroundColor={theme.bg}
            nodeLabel={null}
            cooldownTicks={160}
            onNodeHover={(n) => setHover(n || null)}
            onNodeClick={(n) => select(n.key)}
            onBackgroundClick={() => setSelected(null)}
            onRenderFramePre={(ctx, scale) => {
              const PAD = 22;
              const groups = new Map();
              for (const n of shown.nodes) { if (n.x == null) continue; const k = `${n.stratum}|${n.t}`; if (!groups.has(k)) groups.set(k, []); groups.get(k).push(n); }
              for (const [k, pts] of groups) {
                if (pts.length < 2) continue;
                const [stratum, t] = k.split('|');
                const cx = pts.reduce((s, n) => s + n.x, 0) / pts.length; const cy = pts.reduce((s, n) => s + n.y, 0) / pts.length;
                ctx.save();
                ctx.setLineDash([6 / scale, 5 / scale]); ctx.lineWidth = 1.2 / scale; ctx.lineJoin = 'round';
                ctx.strokeStyle = light ? 'rgba(60,70,90,0.3)' : 'rgba(150,160,182,0.3)'; ctx.fillStyle = light ? 'rgba(60,70,90,0.04)' : 'rgba(140,150,170,0.035)';
                const hull = convexHull(pts); let topY;
                if (hull.length < 3) { const r = Math.max(...pts.map((n) => Math.hypot(n.x - cx, n.y - cy) + radiusOf(n))) + PAD; ctx.beginPath(); ctx.arc(cx, cy, r, 0, 2 * Math.PI); topY = cy - r; } else {
                  const padded = hull.map((p) => { const dx = p.x - cx; const dy = p.y - cy; const d = Math.hypot(dx, dy) || 1; const g = PAD + radiusOf(p); return { x: p.x + (dx / d) * g, y: p.y + (dy / d) * g }; });
                  ctx.beginPath(); ctx.moveTo(padded[0].x, padded[0].y); for (let i = 1; i < padded.length; i += 1) ctx.lineTo(padded[i].x, padded[i].y); ctx.closePath(); topY = Math.min(...padded.map((p) => p.y));
                }
                ctx.fill(); ctx.stroke(); ctx.restore();
                const fs = Math.max(12 / scale, 4);
                ctx.font = `700 ${fs}px -apple-system, sans-serif`; ctx.textAlign = 'center'; ctx.textBaseline = 'bottom'; ctx.fillStyle = light ? 'rgba(60,70,90,0.55)' : 'rgba(139,147,167,0.55)';
                ctx.fillText(`${strata.length > 1 ? `${stratum} · ` : ''}${t}s`.toUpperCase(), cx, topY - 4 / scale);
              }
            }}
            linkColor={(l) => {
              const f = hover ? hover.key : selected;
              if (f) return endKey(l.source) === f || endKey(l.target) === f ? theme.ok : (light ? 'rgba(60,70,90,0.08)' : 'rgba(139,147,167,0.06)');
              return light ? 'rgba(60,70,90,0.22)' : 'rgba(139,147,167,0.22)';
            }}
            linkWidth={(l) => { const f = hover ? hover.key : selected; return f && (endKey(l.source) === f || endKey(l.target) === f) ? 2 : 1; }}
            nodeCanvasObject={(n, ctx, scale) => {
              const r = radiusOf(n); const dim = dimmed(n); const weak = n.stale || n.thin;
              const h = heat ? heat[n.key] || heat[n.slug] || 0 : 0;
              const phase = phased ? phases[n.key] : null;
              const hot = pulse && pulse.has(n.key);
              ctx.globalAlpha = dim ? (phased ? 0.08 : 0.12) : weak && !phase ? 0.5 : 1;
              if (h) { ctx.beginPath(); ctx.arc(n.x, n.y, r + 3 + 6 * (h / maxHeat), 0, 2 * Math.PI); ctx.fillStyle = `rgba(255,120,60,${0.12 + 0.3 * (h / maxHeat)})`; ctx.fill(); }
              if (hot) { ctx.beginPath(); ctx.arc(n.x, n.y, r + 9 / Math.sqrt(scale), 0, 2 * Math.PI); ctx.fillStyle = theme.info; ctx.globalAlpha = 0.28; ctx.fill(); ctx.globalAlpha = 1; }
              ctx.beginPath(); ctx.arc(n.x, n.y, r, 0, 2 * Math.PI); ctx.fillStyle = TYPE_COLORS[n.t]; ctx.fill();
              if (n.errors) { ctx.strokeStyle = theme.danger; ctx.lineWidth = 2 / scale; ctx.stroke(); }
              if (phase) { ctx.strokeStyle = phaseColor(phase); ctx.lineWidth = (phase === 'opened' ? 3.5 : phase === 'candidate' ? 1.2 : 2.2) / scale; ctx.stroke(); }
              if (n.key === selected) { ctx.strokeStyle = theme.ok; ctx.lineWidth = 2.5 / scale; ctx.stroke(); } else if (hover && n.key === hover.key) { ctx.strokeStyle = theme.text; ctx.lineWidth = 1.5 / scale; ctx.stroke(); }
              if (!dim) {
                const focused = (hover && (n.key === hover.key || hoverSet.has(n.key))) || (phase && phase !== 'candidate');
                const full = focused || scale > 1.8;
                const label = !full && n.title.length > 26 ? `${n.title.slice(0, 24).trimEnd()}…` : n.title;
                const fs = Math.max((10 + Math.min(n.backlinks || 0, 20) / 5) / scale, 2.5);
                ctx.font = `${fs}px -apple-system, sans-serif`; ctx.textAlign = 'center'; ctx.textBaseline = 'top';
                const y = n.y + r + 2.5 / scale; const w = ctx.measureText(label).width; const px = 3 / scale; const py = 1.5 / scale;
                ctx.fillStyle = light ? 'rgba(244,245,248,0.85)' : 'rgba(11,13,18,0.82)'; ctx.fillRect(n.x - w / 2 - px, y - py, w + px * 2, fs + py * 2);
                ctx.fillStyle = focused || n.key === selected ? theme.text : theme.muted; ctx.fillText(label, n.x, y);
                if (phase && phase !== 'candidate' && scale > 0.9) { ctx.fillStyle = phaseColor(phase); ctx.font = `${Math.max(8 / scale, 2)}px -apple-system, sans-serif`; ctx.fillText(phase, n.x, y + fs + 2.5 / scale); }
              }
              ctx.globalAlpha = 1;
            }}
            nodePointerAreaPaint={(n, color, ctx) => { ctx.fillStyle = color; ctx.beginPath(); ctx.arc(n.x, n.y, radiusOf(n) + 4, 0, 2 * Math.PI); ctx.fill(); }}
          />
          {card ? (
            <div className={`bm-card ${hover ? 'is-hover' : ''}`} data-nova-note={`Selected page: ${card.title} (${card.t})`}>
              <div className="bm-card-head">
                <i style={{ background: TYPE_COLORS[card.t] }} />
                <strong>{card.title}</strong>
                <span className="bm-type">{card.t}</span>
                {strata.length > 1 ? <span className="bm-type">{card.stratum}</span> : null}
              </div>
              {card.blurb ? <p className="bm-blurb">{card.blurb}</p> : null}
              <div className="bm-meta">
                <span>{card.backlinks} in</span><span>{card.links} out</span><span>{card.wordCount} words</span>
                {card.updated ? <span>updated {card.updated}</span> : null}
                {touchedAt.get(card.key) && touchedAt.get(card.key) !== card.updated ? <span>touched {touchedAt.get(card.key)}</span> : null}
                {card.stale ? <span className="bm-flag">stale</span> : null}{card.thin ? <span className="bm-flag">thin</span> : null}{card.errors ? <span className="bm-flag is-err">{card.errors} error{card.errors > 1 ? 's' : ''}</span> : null}
                {heat && (heat[card.key] || heat[card.slug]) ? <span className="bm-flag is-heat">served {heat[card.key] || heat[card.slug]}×</span> : null}
              </div>
              {card.headings?.length ? <ol className="bm-outline">{card.headings.map((h) => <li key={h}>{h}</li>)}</ol> : null}
              {!hover && sel ? (
                <>
                  <div className="bm-card-links">
                    {selIn.length ? <div><h5>Linked from</h5>{selIn.map((n) => <button key={n.key} type="button" onClick={() => select(n.key, { center: true })}>{n.title}</button>)}</div> : null}
                    {selOut.length ? <div><h5>Links to</h5>{selOut.map((n) => <button key={n.key} type="button" onClick={() => select(n.key, { center: true })}>{n.title}</button>)}</div> : null}
                  </div>
                  {onOpen ? <button type="button" className="bm-open" onClick={() => onOpen(sel)}>Open page →</button> : null}
                </>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// A small neighbourhood graph for a page reader: the page, who links to it
// and what it links to. Same data, no chrome.
export function BrainNeighbourhood({ graph, focus, onOpen, height = 260 }) {
  const sub = useMemo(() => {
    if (!graph || !focus) return null;
    const nodes = (graph.nodes || []).map((n) => ({ ...n, key: keyOf(n) }));
    const have = new Set(nodes.map((n) => n.key));
    const edges = (graph.edges || []).map((e) => ({ source: e.source ?? e.from, target: e.target ?? e.to })).filter((e) => have.has(e.source) && have.has(e.target));
    const keep = new Set([focus]);
    for (const e of edges) { if (e.source === focus) keep.add(e.target); if (e.target === focus) keep.add(e.source); }
    return { nodes: nodes.filter((n) => keep.has(n.key)), edges: edges.filter((e) => keep.has(e.source) && keep.has(e.target)), touches: [] };
  }, [graph, focus]);
  if (!sub || sub.nodes.length < 2) return null;
  return <BrainMap graph={sub} onOpen={onOpen} height={height} initialSelected={focus} />;
}
